import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "@/components/shared/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getLocations, createLocation, updateLocation, deleteLocation, uploadFile, bulkUploadLocations, getLocationOwner } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { MapPin, Plus, Trash2, Edit, Loader2, Search, Upload, Download, User, Phone, Mail, ChevronDown, X, Wand2 } from "lucide-react";
import { exportToCSV } from "@/lib/exportCSV";

const downloadSampleCSV = () => {
  const content = "name,address,city,state,zip_code,phone,email,hours,description,services,active,latitude,longitude,social_facebook,social_instagram\nDowntown Store,123 Main St,Miami,FL,33101,555-0100,store1@example.com,Mon-Sat 9am-7pm,Our flagship location,Activations|Bill Pay|Phone Repair,true,25.7617,-80.1918,https://facebook.com/store1,https://instagram.com/store1\nNorth Location,456 Oak Ave,Orlando,FL,32801,555-0200,store2@example.com,Mon-Fri 10am-6pm,North side branch,Activations|Bill Pay,true,28.5383,-81.3792,,";
  const blob = new Blob([content], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "sample_locations.csv";
  a.click();
  URL.revokeObjectURL(url);
};

const emptyLocation = {
  name: "",
  address: "",
  city: "",
  state: "",
  zip_code: "",
  phone: "",
  email: "",
  hours: "Monday - Friday: 10:00 AM - 7:00 PM\nSaturday: 10:00 AM - 7:00 PM\nSunday: 12:00 PM - 5:00 PM",
  description: "",
  image_url: "",
  services: ["Device Sales", "Unlimited phone plans", "Phone Repairs", "Accessories", "Bill Pay", "Account Support", "Trade-IN Program", "Home Internet", "Family plans", "International plans"],
  active: true,
  status: "active",
  seo_paused: false,
  latitude: "",
  longitude: "",
  social_facebook: "",
  social_instagram: "",
  social_twitter: "",
  social_tiktok: "",
  seo_title: "",
  seo_description: "",
  seo_keywords: "",
  seo_schema: "",
  seo_canonical_url: "",
  og_title: "",
  og_description: "",
  og_image: "",
  og_type: "website",
  cross_streets: "",
  faqs: [],
  google_rating: "",
  google_review_count: "",
  google_maps_url: "",
  payment_methods: ["Cash", "Visa", "Mastercard", "Amex", "Discover", "Apple Pay"],
  service_area_zips: [],
  nearby_cities_seo: [],
  price_range: "$",
  tsp_id: "",
  tw_dc: "",
  sub_agent_id: "",
  corporation_name: "",
  owner_name: "",
  owner_phone: "",
  owner_email: "",
  repair_status: "none",
  vidapay_status: "",
  vidapay_tsp_id: "",
  vidapay_username: "",
  vidapay_password: "",
  b2bsoft_client_id: "",
  b2bsoft_access_code: "",
  b2bsoft_username: "",
  b2bsoft_password: "",
  district_manager_name: "",
  district_manager_phone: "",
  district_manager_email: "",
  store_manager_name: "",
  store_manager_phone: "",
  store_manager_email: "",
};

export default function AdminLocations() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState(emptyLocation);
  const [servicesInput, setServicesInput] = useState("");
  const [activeTab, setActiveTab] = useState("details");
  const [uploading, setUploading] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [ownerInfo, setOwnerInfo] = useState({ owner_name: "", owner_phone: "", owner_email: "" });
  const geocodeTimer = useRef(null);
  const lastGeocodedAddr = useRef("");
  const [filters, setFilters] = useState({ tsp_id: "", tw_dc: "", sub_agent_id: "", corporation_name: "", address: "" });
  const [showFilters, setShowFilters] = useState(false);
  const [generatingKeywords, setGeneratingKeywords] = useState(false);
  const [aiDescLoading, setAiDescLoading] = useState(false);
  const [aiFaqsLoading, setAiFaqsLoading] = useState(false);
  const [applyingTemplates, setApplyingTemplates] = useState(false);

  const handleAiGenerateDescription = async () => {
    const locId = editingLocation?.id || formData.id;
    if (!locId) { toast.error("Save the location first"); return; }
    setAiDescLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/locations/${locId}/ai-generate-description`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to generate");
      }
      const data = await res.json();
      setFormData((prev) => ({ ...prev, description: data.description }));
      toast.success("AI description generated — review and edit as needed");
    } catch (err) {
      toast.error(err.message || "Failed to generate description");
    } finally {
      setAiDescLoading(false);
    }
  };

  const handleAiGenerateFaqs = async () => {
    const locId = editingLocation?.id || formData.id;
    if (!locId) { toast.error("Save the location first"); return; }
    setAiFaqsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/locations/${locId}/ai-generate-faqs`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to generate");
      }
      const data = await res.json();
      setFormData((prev) => ({ ...prev, faqs: data.faqs || [] }));
      toast.success(`Generated ${(data.faqs || []).length} FAQs`);
    } catch (err) {
      toast.error(err.message || "Failed to generate FAQs");
    } finally {
      setAiFaqsLoading(false);
    }
  };

  const handleApplyKeywordTemplates = async (mode = "append") => {
    const locId = editingLocation?.id || formData.id;
    if (!locId) { toast.error("Save the location first"); return; }
    setApplyingTemplates(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/locations/${locId}/apply-keyword-templates`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ mode }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to apply templates");
      }
      const data = await res.json();
      setFormData((prev) => ({ ...prev, seo_keywords: data.keywords }));
      toast.success(`Applied templates: +${data.added} keywords (total ${data.total})`);
    } catch (err) {
      toast.error(err.message || "Failed to apply templates");
    } finally {
      setApplyingTemplates(false);
    }
  };

  const handleGenerateKeywords = async () => {
    const locId = editingLocation?.id || formData.id;
    if (!locId) { toast.error("Save the location first before generating keywords"); return; }
    setGeneratingKeywords(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/locations/${locId}/generate-keywords`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to generate");
      const data = await res.json();
      setFormData((prev) => ({ ...prev, seo_keywords: data.keywords }));
      toast.success(`Generated keywords with ${data.nearby_cities?.length || 0} nearby cities and ${data.nearby_zips?.length || 0} nearby zip codes`);
    } catch (err) {
      toast.error("Failed to generate keywords");
    } finally {
      setGeneratingKeywords(false);
    }
  };

  const [fillingLocalSeo, setFillingLocalSeo] = useState(false);
  const fillLocalSeo = async () => {
    setFillingLocalSeo(true);
    try {
      const qs = new URLSearchParams({ zip_code: formData.zip_code || "", city: formData.city || "" });
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/locations/local-seo/suggest?${qs}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      if (!data.service_area_zips.length) return toast.error("No nearby areas found for that zip code");
      setFormData((prev) => ({ ...prev, service_area_zips: data.service_area_zips, nearby_cities_seo: data.nearby_cities }));
      toast.success(`Filled ${data.service_area_zips.length} zip codes and ${data.nearby_cities.length} nearby cities`);
    } catch {
      toast.error("Couldn't look up nearby areas");
    } finally {
      setFillingLocalSeo(false);
    }
  };

  // Combined: run Auto-Generate (nearby cities/zips) then Apply Keyword Templates (append),
  // so the final keyword list contains both sets merged/de-duped by the backend.
  const handleBuildKeywords = async () => {
    const locId = editingLocation?.id || formData.id;
    if (!locId) { toast.error("Save the location first"); return; }
    setGeneratingKeywords(true);
    setApplyingTemplates(true);
    try {
      const token = localStorage.getItem("token");
      // Step 1: Auto-Generate
      const genRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/locations/${locId}/generate-keywords`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!genRes.ok) throw new Error("Auto-generate failed");
      const genData = await genRes.json();
      // Step 2: Apply Keyword Templates (append to what was just generated)
      const tplRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/locations/${locId}/apply-keyword-templates`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ mode: "append" }),
      });
      if (!tplRes.ok) {
        const err = await tplRes.json().catch(() => ({}));
        throw new Error(err.detail || "Apply templates failed");
      }
      const tplData = await tplRes.json();
      setFormData((prev) => ({ ...prev, seo_keywords: tplData.keywords }));
      toast.success(`Built ${tplData.total} keywords — nearby (${genData.nearby_cities?.length || 0} cities, ${genData.nearby_zips?.length || 0} zips) + ${tplData.added} from your templates`);
    } catch (err) {
      toast.error(err.message || "Failed to build keywords");
    } finally {
      setGeneratingKeywords(false);
      setApplyingTemplates(false);
    }
  };

  const filteredLocations = locations.filter((loc) => {
    const f = filters;
    if (f.tsp_id && !(loc.tsp_id || "").toLowerCase().includes(f.tsp_id.toLowerCase())) return false;
    if (f.tw_dc && !(loc.tw_dc || "").toLowerCase().includes(f.tw_dc.toLowerCase())) return false;
    if (f.sub_agent_id && !(loc.sub_agent_id || "").toLowerCase().includes(f.sub_agent_id.toLowerCase())) return false;
    if (f.corporation_name && !(loc.corporation_name || loc.name || "").toLowerCase().includes(f.corporation_name.toLowerCase())) return false;
    if (f.address) {
      const fullAddr = `${loc.address || ""} ${loc.city || ""} ${loc.state || ""} ${loc.zip_code || ""}`.toLowerCase();
      if (!fullAddr.includes(f.address.toLowerCase())) return false;
    }
    return true;
  });
  const hasActiveFilters = Object.values(filters).some((v) => v);

  // Auto-geocode address when address fields change
  const geocodeAddress = useCallback(async (address, city, state, zip) => {
    const query = [address, city, state, zip].filter(Boolean).join(", ");
    if (!address || !city || !state || query === lastGeocodedAddr.current) return;
    lastGeocodedAddr.current = query;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/geocode?q=${encodeURIComponent(query)}`);
      const data = await res.json();
      if (data && data.lat && data.lon) {
        setFormData((prev) => ({ ...prev, latitude: data.lat, longitude: data.lon }));
        toast.success("Coordinates auto-filled from address");
      }
    } catch { /* silently fail */ }
  }, []);

  useEffect(() => {
    if (geocodeTimer.current) clearTimeout(geocodeTimer.current);
    const { address, city, state, latitude, longitude } = formData;
    // Auto-geocode if lat/lng are empty, 0, or blank string
    if (address && city && state && !latitude && !longitude) {
      lastGeocodedAddr.current = "";
      geocodeTimer.current = setTimeout(() => {
        geocodeAddress(address, city, state, formData.zip_code);
      }, 800);
    }
    return () => { if (geocodeTimer.current) clearTimeout(geocodeTimer.current); };
  }, [formData.address, formData.city, formData.state, formData.zip_code, geocodeAddress]);

  const generateDescription = (data) => {
    const city = data.city || "City";
    const state = data.state || "State";
    const address = data.address || "Street Address";
    const zip = data.zip_code || "Zip Code";
    const phone = data.phone || "Phone";
    return `Our exclusive location in ${city}, ${state}, conveniently located at ${address} ${city}, ${state} ${zip}. We offer comprehensive Total Wireless services including device sales, plan activation, repairs, and accessories. Our expert team is ready to assist you with all your wireless needs. We can be reached at ${phone}.`;
  };

  const generateSeoTitle = (data) => {
    const city = data.city || "City";
    const state = data.state || "State";
    return `Switch & Save with Total Wireless Store in ${city}, ${state} | Prime Media Wireless | No-Contract Unlimited Plans & Devices`;
  };

  const generateSeoDescription = (data) => {
    const city = data.city || "City";
    const state = data.state || "State";
    return `Stop overpaying! Visit Total Wireless in ${city}, ${state} for no-contract plans & phones. Fast activation. Start saving today! Experience wireless freedom, premium perks, affordable plans, and no long-term contracts at Total Wireless in ${city}, ${state}. With affordable Multiline plans, you can add a 4th line at no additional cost. Covered by the Verizon network, we help you find the right fit for your lifestyle. Stop by today and explore our wide selection of the latest devices and accessories. You can even keep your own phone and number. At Total Wireless in ${city}, ${state}, we're totally with you.`;
  };

  const generateSeoSchema = (data) => {
    const city = data.city || "City";
    const state = data.state || "State";
    const zip = data.zip_code || "";
    const crossStreets = data.cross_streets || "";
    const sameAs = [data.social_facebook, data.social_instagram, data.social_twitter, data.social_tiktok].filter(Boolean);
    
    // Parse SEO keywords if present
    const seoKeywords = (data.seo_keywords || "").split(",").map(k => k.trim()).filter(Boolean);

    // Cross street knowsAbout entries
    const crossStreetEntries = crossStreets ? [
      `Total Wireless ${crossStreets}`,
      `Total Wireless near ${crossStreets}`,
      `Total by Verizon ${crossStreets}`,
      `phone repair ${crossStreets}`,
      `phone repair near ${crossStreets}`,
      `wireless store ${crossStreets}`,
    ] : [];

    const schema = {
      "@context": "https://schema.org",
      "@type": "ElectronicsStore",
      "name": `Total Wireless ${city}`,
      "image": data.image_url || "",
      "@id": "",
      "url": "https://primemediawireless.com",
      "telephone": data.phone || "",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": data.address || "",
        "addressLocality": city,
        "addressRegion": state,
        "postalCode": zip,
        "addressCountry": "US"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": String(data.latitude || ""),
        "longitude": String(data.longitude || "")
      },
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
          "opens": "10:00",
          "closes": "19:00"
        },
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Sunday"],
          "opens": "12:00",
          "closes": "17:00"
        }
      ],
      "priceRange": "$",
      ...(sameAs.length > 0 && { "sameAs": sameAs }),
      "knowsAbout": [
        "Total Wireless", "Total Wireless Store",
        `Total Wireless Store ${city} ${state}`,
        "Total Wireless Dealer",
        `Total Wireless Store ${city}`,
        "Total Wireless Exclusive Retailer",
        `Total Wireless Store ${state}`,
        "specializing in prepaid wireless",
        `Total Wireless Store ${city} ${state} ${zip}`,
        `Total Wireless Store ${zip}`,
        "total wireless near me", "total wireless store near me",
        "total wireless by verizon near me", "total by verizon near me",
        "Total by Verizon retailer near me",
        `Total Wireless locations in ${city}`,
        "Prepaid phone stores near me",
        `${city} Total Wireless authorized dealer`,
        "Phone Repair Near Me", "electronics repair shop near me",
        "tech repair near me", "phone/laptop repair near me",
        "game console repair near me", "Cell Phone Repair near me",
        "Cell phone store", "Mobile phones",
        "Cell phone accessories", "Wireless store",
        "Metro by T-Mobile", "Boost Mobile",
        "metro pcs", "metro by t-mobile near me",
        "boost mobile near me", "cricket wireless", "cricket wireless near me",
        ...crossStreetEntries,
        ...seoKeywords
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Total Wireless Products & Services",
        "itemListElement": [
          `Mobile phone store near me`, `Cell phone shop in ${city}`,
          "Wireless carrier store near me", "Total Wireless Device Sales",
          "Apple iPhone", "Apple iPad", "Samsung Galaxy", "Google Pixel",
          "Motorola Moto G", "Motorola Edge", "Motorola Razr",
          "TCL Devices", "Blu Devices", "Phone", "Tablet", "Hotspot", "Home Internet",
          "Bring your own phone", "Bring your own device", "Bring your own tablet",
          "Phone Repair", "Tablet Repair", "Computer Repair", "Game Console Repair",
          "Total Wireless Bill Pay", "Trade-in my phone", "Trade-in program",
          "Family Plans", "Unlimited Plans", "Accessories",
          "Total Wireless Account Support", "Total Wireless Home Internet Router",
          "Total Wireless Home Internet", "CPO Devices", "Total Wireless Protect",
          "Device Financing", "Phone Financing", "Glow Financing",
          "Total Wireless Phone Financing", "Glow Financial Services",
          "Glow Services", "Total Wireless Edge",
          "24 monthly installments with no down payment required"
        ].map(name => ({ "@type": "Offer", "name": name }))
      }
    };
    return JSON.stringify(schema, null, 2);
  };

  const generateCanonicalUrl = (data) => {
    if (!data.city || !data.address) return "";
    const state = (data.state || "").toLowerCase().replace(/\s+/g, "");
    const city = (data.city || "").toLowerCase().replace(/\s+/g, "-");
    const addr = (data.address || "").toLowerCase().replace(/[,#.]+/g, "").replace(/\s+/g, "-");
    return `https://primemediawireless.com/locations/${state}/${city}/${addr}`;
  };

  const generateOgTags = (data) => {
    const city = data.city || "";
    const state = data.state || "";
    return {
      og_title: data.seo_title || `Total Wireless ${city}, ${state} | Prime Media Wireless`,
      og_description: (data.seo_description || `Visit Total Wireless in ${city}, ${state}. No-contract plans, devices, and accessories. Covered by Verizon.`).slice(0, 200),
      og_image: data.image_url || "",
      og_type: "website",
    };
  };

  // Auto-update description and SEO fields when key fields change (only for new locations)
  useEffect(() => {
    if (editingLocation) return;
    const desc = generateDescription(formData);
    const seoTitle = generateSeoTitle(formData);
    const seoDesc = generateSeoDescription(formData);
    const seoSchema = generateSeoSchema(formData);
    const canonicalUrl = generateCanonicalUrl(formData);
    const og = generateOgTags({ ...formData, seo_title: seoTitle, seo_description: seoDesc });
    setFormData((prev) => ({
      ...prev,
      description: desc,
      seo_title: seoTitle,
      seo_description: seoDesc,
      seo_schema: seoSchema,
      seo_canonical_url: canonicalUrl,
      og_title: og.og_title,
      og_description: og.og_description,
      og_image: og.og_image,
      og_type: og.og_type,
    }));
  }, [formData.city, formData.state, formData.address, formData.zip_code, formData.phone, formData.latitude, formData.longitude, formData.image_url, formData.social_facebook, formData.social_instagram, editingLocation]);

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBulkUploading(true);
    try {
      const result = await bulkUploadLocations(file);
      toast.success(`Uploaded ${result.imported || 0} locations`);
      fetchLocations();
    } catch (err) { toast.error(err.response?.data?.detail || "Bulk upload failed"); }
    setBulkUploading(false);
    e.target.value = "";
  };

  const fetchLocations = async () => {
    try {
      const data = await getLocations(false);
      setLocations(data);
    } catch (error) {
      console.error("Error fetching locations:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  const handleOpenDialog = (location = null) => {
    if (location) {
      setEditingLocation(location);
      // Derive lifecycle status when editing legacy records that pre-date
      // the field so the dropdown always has a valid default.
      const derivedStatus = location.status
        || (location.active === false ? "closed" : "active");
      setFormData({
        ...location,
        status: derivedStatus,
        seo_paused: !!location.seo_paused,
      });
      setServicesInput(location.services?.join(", ") || "");
      getLocationOwner(location.id).then((owner) => {
        setOwnerInfo(owner);
        setFormData((prev) => ({
          ...prev,
          corporation_name: owner.company_name || prev.corporation_name,
          owner_name: owner.owner_name || prev.owner_name || "",
          owner_phone: owner.owner_phone || prev.owner_phone || "",
          owner_email: owner.owner_email || prev.owner_email || "",
        }));
      }).catch(() => setOwnerInfo({ owner_name: "", owner_phone: "", owner_email: "" }));
    } else {
      setEditingLocation(null);
      setFormData(emptyLocation);
      setServicesInput(emptyLocation.services.join(", "));
    }
    setActiveTab("details");
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const locationData = {
      ...formData,
      services: servicesInput.split(",").map((s) => s.trim()).filter(Boolean),
      latitude: formData.latitude || null,
      longitude: formData.longitude || null,
      google_rating: formData.google_rating === "" || formData.google_rating == null ? null : Number(formData.google_rating),
      google_review_count: formData.google_review_count === "" || formData.google_review_count == null ? null : parseInt(formData.google_review_count, 10),
    };

    try {
      if (editingLocation) {
        await updateLocation(editingLocation.id, locationData);
        toast.success("Location updated successfully");
      } else {
        await createLocation(locationData);
        toast.success("Location created successfully");
      }
      setIsDialogOpen(false);
      fetchLocations();
    } catch (error) {
      toast.error(editingLocation ? "Failed to update location" : "Failed to create location");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteLocation = async (locationId) => {
    if (!window.confirm("Are you sure you want to delete this location?")) return;

    try {
      await deleteLocation(locationId);
      toast.success("Location deleted");
      fetchLocations();
    } catch (error) {
      toast.error("Failed to delete location");
    }
  };

  return (
    <AdminLayout title="Locations" breadcrumb="Locations">
      <div className="space-y-6" data-testid="admin-locations-page">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2 text-sm text-slate-500 font-body">
            <MapPin className="w-4 h-4" />
            {locations.length} locations
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => exportToCSV(filteredLocations, [
              { label: "Name", accessor: "name" },
              { label: "TSP ID", accessor: "tsp_id" },
              { label: "TW DC", accessor: "tw_dc" },
              { label: "Sub-Agent ID", accessor: "sub_agent_id" },
              { label: "Corporation Name", accessor: "corporation_name" },
              { label: "Address", accessor: "address" },
              { label: "City", accessor: "city" },
              { label: "State", accessor: "state" },
              { label: "Zip", accessor: "zip_code" },
              { label: "Store Phone", accessor: "phone" },
              { label: "Services", accessor: (r) => (r.services || []).join("; ") },
              { label: "Hours", accessor: "hours" },
              { label: "Status", accessor: "status" },
            ], "locations")} data-testid="export-locations-btn">
              <Download className="w-4 h-4 mr-2" />Export
            </Button>
            <input type="file" accept=".csv" onChange={handleBulkUpload} className="hidden" id="bulk-loc-upload" data-testid="bulk-loc-upload" />
            <Button variant="outline" size="sm" onClick={downloadSampleCSV} data-testid="download-sample-locations-csv">
              <Download className="w-4 h-4 mr-2" />Sample CSV
            </Button>
            <Button variant="outline" onClick={() => document.getElementById("bulk-loc-upload").click()} disabled={bulkUploading} data-testid="bulk-upload-loc-btn">
              {bulkUploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Bulk CSV
            </Button>
            <Button
              onClick={() => handleOpenDialog()}
              className="bg-teal hover:bg-teal-dark text-white"
              data-testid="create-location-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Location
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border border-slate-200 p-3" data-testid="locations-filters">
          <button onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-slate-900 w-full">
            <Search className="w-4 h-4" />
            Search & Filter
            {hasActiveFilters && <span className="bg-teal text-white text-xs px-1.5 py-0.5 rounded-full">{Object.values(filters).filter(v => v).length}</span>}
            <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${showFilters ? "rotate-180" : ""}`} />
          </button>
          {showFilters && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-3">
              <div>
                <Label className="text-xs text-slate-500 mb-1">TSP ID</Label>
                <Input placeholder="Search TSP ID..." value={filters.tsp_id} onChange={(e) => setFilters({ ...filters, tsp_id: e.target.value })} className="h-8 text-sm" data-testid="filter-tsp-id" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1">TW DC</Label>
                <Input placeholder="Search TW DC..." value={filters.tw_dc} onChange={(e) => setFilters({ ...filters, tw_dc: e.target.value })} className="h-8 text-sm" data-testid="filter-tw-dc" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1">Sub-Agent ID</Label>
                <Input placeholder="Search Sub-Agent ID..." value={filters.sub_agent_id} onChange={(e) => setFilters({ ...filters, sub_agent_id: e.target.value })} className="h-8 text-sm" data-testid="filter-sub-agent-id" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1">Corporation Name</Label>
                <Input placeholder="Search name..." value={filters.corporation_name} onChange={(e) => setFilters({ ...filters, corporation_name: e.target.value })} className="h-8 text-sm" data-testid="filter-corporation-name" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1">Address</Label>
                <Input placeholder="Search address..." value={filters.address} onChange={(e) => setFilters({ ...filters, address: e.target.value })} className="h-8 text-sm" data-testid="filter-address" />
              </div>
              {hasActiveFilters && (
                <div className="col-span-full">
                  <Button variant="ghost" size="sm" onClick={() => setFilters({ tsp_id: "", tw_dc: "", sub_agent_id: "", corporation_name: "", address: "" })} className="text-slate-500 text-xs" data-testid="clear-filters-btn">Clear all filters</Button>
                  <span className="text-xs text-slate-400 ml-2">Showing {filteredLocations.length} of {locations.length} locations</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Locations Grid */}
        {loading ? (
          <p className="text-slate-500 text-center py-8 font-body">Loading locations...</p>
        ) : locations.length === 0 ? (
          <div className="text-center py-12">
            <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 font-body">No locations yet</p>
            <p className="text-slate-400 text-sm font-body">Create your first location to get started</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto" data-testid="locations-table">
            <table className="w-full text-sm whitespace-nowrap">
              <thead>
                <tr className="bg-slate-50 text-left">
                  <th className="px-3 py-3 font-medium text-slate-600">Location</th>
                  <th className="px-3 py-3 font-medium text-slate-600">Address</th>
                  <th className="px-3 py-3 font-medium text-slate-600">Store Phone</th>
                  <th className="px-3 py-3 font-medium text-slate-600">TSP</th>
                  <th className="px-3 py-3 font-medium text-slate-600">TWDC</th>
                  <th className="px-3 py-3 font-medium text-slate-600">SA</th>
                  <th className="px-3 py-3 font-medium text-slate-600">Owner Name</th>
                  <th className="px-3 py-3 font-medium text-slate-600">Owner Phone</th>
                  <th className="px-3 py-3 font-medium text-slate-600">Status</th>
                  <th className="px-3 py-3 font-medium text-slate-600 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLocations.map((location) => (
                  <tr key={location.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`location-row-${location.id}`}>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${location.active ? "bg-teal/10" : "bg-slate-100"}`}>
                          <MapPin className={`w-4 h-4 ${location.active ? "text-teal" : "text-slate-400"}`} />
                        </div>
                        <div className="flex flex-col">
                          <span className="font-medium text-navy whitespace-nowrap leading-tight">{location.name}</span>
                          {location.corporation_name && (
                            <span className="text-xs text-slate-500 whitespace-nowrap leading-tight mt-0.5">{location.corporation_name}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-slate-600">{location.address}, {location.city}, {location.state} {location.zip_code}</td>
                    <td className="px-3 py-3 text-slate-600 whitespace-nowrap">{location.phone || <span className="text-slate-400">Coming soon</span>}</td>
                    <td className="px-3 py-3 text-slate-600">{location.tsp_id || "—"}</td>
                    <td className="px-3 py-3 text-slate-600">{location.tw_dc || "—"}</td>
                    <td className="px-3 py-3 text-slate-600">{location.sub_agent_id || "—"}</td>
                    <td className="px-3 py-3 text-navy text-sm whitespace-nowrap">{location.owner_name || "—"}</td>
                    <td className="px-3 py-3 text-slate-600 text-sm whitespace-nowrap">{location.owner_phone || "—"}</td>
                    <td className="px-3 py-3">
                      {(() => {
                        const s = location.status || (location.active === false ? "closed" : "active");
                        if (s === "closed") {
                          return <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">Closed</span>;
                        }
                        if (s === "temporarily_closed") {
                          return (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
                              Temp. Closed{location.seo_paused ? " · SEO paused" : ""}
                            </span>
                          );
                        }
                        return <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">Active</span>;
                      })()}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(location)} className="h-8 px-2" title="Edit" aria-label="Edit location" data-testid={`edit-location-${location.id}`}>
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteLocation(location.id)} className="h-8 px-2 text-red-400 hover:text-red-600 hover:bg-red-50" data-testid={`delete-location-${location.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-navy">
                {editingLocation ? "Edit Location" : "Create Location"}
              </DialogTitle>
            </DialogHeader>
            
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid grid-cols-7 mb-4">
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="contacts">Dealer Contact</TabsTrigger>
                <TabsTrigger value="social">Social Media</TabsTrigger>
                <TabsTrigger value="seo">SEO</TabsTrigger>
                <TabsTrigger value="faqs" data-testid="location-faqs-tab">FAQs</TabsTrigger>
                <TabsTrigger value="vidapay">External Systems</TabsTrigger>
                <TabsTrigger value="repair">Device Repair</TabsTrigger>
              </TabsList>

              <form onSubmit={handleSubmit}>
                <TabsContent value="details" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="tsp_id">TSP ID</Label>
                      <Input
                        id="tsp_id"
                        value={formData.tsp_id || ""}
                        onChange={(e) => setFormData({ ...formData, tsp_id: e.target.value })}
                        placeholder="TSP-001"
                        data-testid="location-tsp-id-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="tw_dc">TW DC</Label>
                      <Input
                        id="tw_dc"
                        value={formData.tw_dc || ""}
                        onChange={(e) => setFormData({ ...formData, tw_dc: e.target.value })}
                        placeholder="TW-DC-001"
                        data-testid="location-tw-dc-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="sub_agent_id">Sub-Agent ID</Label>
                      <Input
                        id="sub_agent_id"
                        value={formData.sub_agent_id || ""}
                        onChange={(e) => setFormData({ ...formData, sub_agent_id: e.target.value })}
                        placeholder="SA-001"
                        data-testid="location-sub-agent-id-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="corporation_name">Corporation Name</Label>
                      <Input
                        id="corporation_name"
                        value={formData.corporation_name || ""}
                        onChange={(e) => setFormData({ ...formData, corporation_name: e.target.value })}
                        placeholder="Auto-filled from assigned dealer"
                        data-testid="location-corporation-name-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">Auto-populated from assigned dealer's Company Name</p>
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="name">Location Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        placeholder="Prime Media Wireless - City Name"
                        data-testid="location-name-input"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="address">Street Address *</Label>
                      <Input
                        id="address"
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        required
                        placeholder="123 Main Street"
                        data-testid="location-address-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        required
                        data-testid="location-city-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="state">State *</Label>
                      <Input
                        id="state"
                        value={formData.state}
                        onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                        required
                        placeholder="NY"
                        data-testid="location-state-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="zip_code">ZIP Code *</Label>
                      <Input
                        id="zip_code"
                        value={formData.zip_code}
                        onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                        required
                        data-testid="location-zip-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Store Phone</Label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="Blank shows Coming soon"
                        data-testid="location-phone-input"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="cross_streets">Cross Streets</Label>
                      <Input
                        id="cross_streets"
                        value={formData.cross_streets || ""}
                        onChange={(e) => setFormData({ ...formData, cross_streets: e.target.value })}
                        placeholder="e.g. Lahser and Civic Center"
                        data-testid="location-cross-streets-input"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="email">Store Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="location@primemediawireless.com"
                        data-testid="location-email-input"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="hours">Business Hours</Label>
                      <Textarea
                        id="hours"
                        value={formData.hours}
                        onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
                        placeholder="Mon-Fri: 9AM-7PM&#10;Sat: 10AM-5PM&#10;Sun: Closed"
                        rows={3}
                        data-testid="location-hours-input"
                      />
                    </div>
                    <div className="col-span-2">
                      <div className="flex items-center justify-between mb-1">
                        <Label htmlFor="description">Description (About This Location)</Label>
                        {formData.id && (
                          <button
                            type="button"
                            className="text-[11px] text-teal hover:underline font-medium inline-flex items-center gap-1 disabled:opacity-50"
                            onClick={() => handleAiGenerateDescription()}
                            disabled={aiDescLoading}
                            data-testid="ai-generate-description-btn"
                          >
                            {aiDescLoading ? "Generating..." : "✨ Generate with AI"}
                          </button>
                        )}
                      </div>
                      <Textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Describe this location... (or click Generate with AI after saving)"
                        rows={6}
                        data-testid="location-description-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        A 300-400 word unique local description boosts SEO. Use the AI helper to draft one based on city, street, and services.
                      </p>
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="services">Services (comma separated)</Label>
                      <Input
                        id="services"
                        value={servicesInput}
                        onChange={(e) => setServicesInput(e.target.value)}
                        placeholder="Device Sales, Plan Activation, Repairs, Accessories"
                        data-testid="location-services-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="latitude">Latitude</Label>
                      <Input
                        id="latitude"
                        type="number"
                        step="any"
                        value={formData.latitude || ""}
                        onChange={(e) => setFormData({ ...formData, latitude: e.target.value ? parseFloat(e.target.value) : "" })}
                        placeholder="40.7128"
                        data-testid="location-latitude-input"
                      />
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor="longitude">Longitude</Label>
                        <button
                          type="button"
                          className="text-[11px] text-teal hover:underline font-medium"
                          onClick={() => {
                            lastGeocodedAddr.current = "";
                            setFormData((prev) => ({ ...prev, latitude: "", longitude: "" }));
                            setTimeout(() => geocodeAddress(formData.address, formData.city, formData.state, formData.zip_code), 100);
                          }}
                          data-testid="location-geocode-btn"
                        >
                          Geocode from address
                        </button>
                      </div>
                      <Input
                        id="longitude"
                        type="number"
                        step="any"
                        value={formData.longitude || ""}
                        onChange={(e) => setFormData({ ...formData, longitude: e.target.value ? parseFloat(e.target.value) : "" })}
                        placeholder="-74.0060"
                        data-testid="location-longitude-input"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="image_url">Location Image</Label>
                      <div className="space-y-2">
                        <div className="border-2 border-dashed border-slate-300 rounded-lg p-3 text-center">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (!file) return;
                              setUploading(true);
                              try {
                                const result = await uploadFile(file, "locations");
                                const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${result.url}`;
                                setFormData({ ...formData, image_url: imageUrl });
                                toast.success("Image uploaded");
                              } catch (err) {
                                toast.error("Failed to upload image");
                              } finally {
                                setUploading(false);
                              }
                            }}
                            className="hidden"
                            id="location-image-upload"
                            data-testid="location-image-upload"
                          />
                          <label htmlFor="location-image-upload" className="cursor-pointer">
                            {uploading ? (
                              <div className="flex items-center justify-center gap-2">
                                <Loader2 className="w-4 h-4 animate-spin text-teal" />
                                <span className="text-sm text-slate-600 font-body">Uploading...</span>
                              </div>
                            ) : (
                              <div className="flex items-center justify-center gap-2">
                                <Upload className="w-5 h-5 text-slate-400" />
                                <span className="text-sm text-slate-600 font-body">Upload Image</span>
                              </div>
                            )}
                          </label>
                        </div>
                        <Input
                          id="image_url"
                          value={formData.image_url}
                          onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                          placeholder="Or paste image URL here"
                          data-testid="location-image-input"
                        />
                        {formData.image_url && (
                          <img
                            src={formData.image_url}
                            alt="Preview"
                            className="w-full h-32 object-cover rounded-lg"
                            onError={(e) => (e.target.style.display = "none")}
                          />
                        )}
                      </div>
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label htmlFor="location-status">Location Status</Label>
                      <div className="flex flex-wrap items-center gap-4">
                        <select
                          id="location-status"
                          value={formData.status || "active"}
                          onChange={(e) => {
                            const s = e.target.value;
                            setFormData({
                              ...formData,
                              status: s,
                              // Keep legacy `active` in sync so any consumer
                              // still reading `.active` (list badges, public
                              // filters) reflects the new state immediately.
                              active: s !== "closed",
                              // Clear seo_paused whenever leaving the
                              // temporarily_closed state.
                              seo_paused: s === "temporarily_closed" ? !!formData.seo_paused : false,
                            });
                          }}
                          className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal"
                          data-testid="location-status-select"
                        >
                          <option value="active">Active</option>
                          <option value="temporarily_closed">Temporarily Closed</option>
                          <option value="closed">Closed</option>
                        </select>
                        {formData.status === "temporarily_closed" && (
                          <label className="flex items-center gap-2 text-sm text-slate-700" data-testid="location-seo-paused-wrapper">
                            <input
                              type="checkbox"
                              className="w-4 h-4 rounded border-slate-300 text-teal focus:ring-teal"
                              checked={!!formData.seo_paused}
                              onChange={(e) => setFormData({ ...formData, seo_paused: e.target.checked })}
                              data-testid="location-seo-paused-checkbox"
                            />
                            Pause SEO (exclude from sitemap while temporarily closed)
                          </label>
                        )}
                      </div>
                      <p className="text-xs text-slate-500">
                        <span className="font-medium">Active</span> — visible on the public site and included in SEO.{" "}
                        <span className="font-medium">Temporarily Closed</span> — still visible; optionally pause SEO to remove from sitemap.{" "}
                        <span className="font-medium">Closed</span> — hidden from the public site and search engines until reactivated.
                      </p>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="contacts" className="space-y-6">
                  {/* Owner Info - Auto-populated from assigned dealer */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <User className="w-4 h-4 text-navy" />
                      <h4 className="font-heading font-semibold text-navy text-sm">Owner Information</h4>
                      <span className="text-xs bg-teal/10 text-teal px-2 py-0.5 rounded-full">From Assigned Dealer</span>
                    </div>
                    <p className="text-xs text-slate-400 mb-3">
                      Auto-populated from the assigned dealer&rsquo;s profile. To change these, update the dealer in Admin &rarr; Dealers, or reassign this location.
                    </p>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Name</Label>
                        <Input value={formData.owner_name || ""} readOnly disabled data-testid="owner-name" className="bg-slate-50 cursor-not-allowed" />
                      </div>
                      <div>
                        <Label>Phone</Label>
                        <Input value={formData.owner_phone || ""} readOnly disabled data-testid="owner-phone" className="bg-slate-50 cursor-not-allowed" />
                      </div>
                      <div>
                        <Label>Email</Label>
                        <Input value={formData.owner_email || ""} readOnly disabled data-testid="owner-email" className="bg-slate-50 cursor-not-allowed" />
                      </div>
                    </div>
                  </div>

                  {/* District Manager - Editable */}
                  <div className="border-t pt-4">
                    <div className="flex items-center gap-2 mb-3">
                      <User className="w-4 h-4 text-indigo-500" />
                      <h4 className="font-heading font-semibold text-navy text-sm">District Manager</h4>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Name</Label>
                        <Input value={formData.district_manager_name || ""} onChange={(e) => setFormData({ ...formData, district_manager_name: e.target.value })} placeholder="District manager name" data-testid="dm-name" />
                      </div>
                      <div>
                        <Label>Phone</Label>
                        <Input value={formData.district_manager_phone || ""} onChange={(e) => setFormData({ ...formData, district_manager_phone: e.target.value })} placeholder="Phone number" data-testid="dm-phone" />
                      </div>
                      <div>
                        <Label>Email</Label>
                        <Input type="email" value={formData.district_manager_email || ""} onChange={(e) => setFormData({ ...formData, district_manager_email: e.target.value })} placeholder="Email address" data-testid="dm-email" />
                      </div>
                    </div>
                  </div>

                  {/* Store Manager - Editable */}
                  <div className="border-t pt-4">
                    <div className="flex items-center gap-2 mb-3">
                      <User className="w-4 h-4 text-teal" />
                      <h4 className="font-heading font-semibold text-navy text-sm">Store Manager</h4>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Name</Label>
                        <Input value={formData.store_manager_name || ""} onChange={(e) => setFormData({ ...formData, store_manager_name: e.target.value })} placeholder="Store manager name" data-testid="sm-name" />
                      </div>
                      <div>
                        <Label>Phone</Label>
                        <Input value={formData.store_manager_phone || ""} onChange={(e) => setFormData({ ...formData, store_manager_phone: e.target.value })} placeholder="Phone number" data-testid="sm-phone" />
                      </div>
                      <div>
                        <Label>Email</Label>
                        <Input type="email" value={formData.store_manager_email || ""} onChange={(e) => setFormData({ ...formData, store_manager_email: e.target.value })} placeholder="Email address" data-testid="sm-email" />
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="social" className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-lg mb-4">
                    <p className="text-sm text-slate-600 font-body">
                      Link the dealer's social media accounts for this location.
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="social_facebook">Facebook URL</Label>
                    <Input
                      id="social_facebook"
                      value={formData.social_facebook || ""}
                      onChange={(e) => setFormData({ ...formData, social_facebook: e.target.value })}
                      placeholder="https://facebook.com/yourpage"
                      data-testid="location-social-facebook"
                    />
                  </div>
                  <div>
                    <Label htmlFor="social_instagram">Instagram URL</Label>
                    <Input
                      id="social_instagram"
                      value={formData.social_instagram || ""}
                      onChange={(e) => setFormData({ ...formData, social_instagram: e.target.value })}
                      placeholder="https://instagram.com/yourprofile"
                      data-testid="location-social-instagram"
                    />
                  </div>
                  <div>
                    <Label htmlFor="social_twitter">Twitter/X URL</Label>
                    <Input
                      id="social_twitter"
                      value={formData.social_twitter || ""}
                      onChange={(e) => setFormData({ ...formData, social_twitter: e.target.value })}
                      placeholder="https://twitter.com/yourhandle"
                      data-testid="location-social-twitter"
                    />
                  </div>
                  <div>
                    <Label htmlFor="social_tiktok">TikTok URL</Label>
                    <Input
                      id="social_tiktok"
                      value={formData.social_tiktok || ""}
                      onChange={(e) => setFormData({ ...formData, social_tiktok: e.target.value })}
                      placeholder="https://tiktok.com/@yourhandle"
                      data-testid="location-social-tiktok"
                    />
                  </div>
                </TabsContent>

                <TabsContent value="seo" className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-lg mb-4">
                    <p className="text-sm text-slate-600 font-body">
                      SEO fields are auto-populated from location details. Edit them to customize for better search engine visibility.
                    </p>
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="seo_title">SEO Title</Label>
                      <button type="button" className="text-[11px] text-teal hover:underline font-medium" onClick={() => setFormData((prev) => ({ ...prev, seo_title: generateSeoTitle(prev) }))} data-testid="regenerate-seo-title">Regenerate</button>
                    </div>
                    <Input
                      id="seo_title"
                      value={formData.seo_title}
                      onChange={(e) => setFormData({ ...formData, seo_title: e.target.value })}
                      placeholder="Switch & Save with Total Wireless Store in [City], [State] | Prime Media Wireless"
                      data-testid="location-seo-title"
                    />
                    <p className="text-xs text-slate-500 mt-1">{(formData.seo_title || "").length} characters</p>
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="seo_description">SEO Description</Label>
                      <button type="button" className="text-[11px] text-teal hover:underline font-medium" onClick={() => setFormData((prev) => ({ ...prev, seo_description: generateSeoDescription(prev) }))} data-testid="regenerate-seo-description">Regenerate</button>
                    </div>
                    <Textarea
                      id="seo_description"
                      value={formData.seo_description}
                      onChange={(e) => setFormData({ ...formData, seo_description: e.target.value })}
                      placeholder="Stop overpaying! Visit Total Wireless in [City], [State] for no-contract plans & phones..."
                      rows={5}
                      data-testid="location-seo-description"
                    />
                    <p className="text-xs text-slate-500 mt-1">{(formData.seo_description || "").length} characters</p>
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="seo_keywords">SEO Keywords</Label>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 text-[11px] text-teal hover:underline font-medium disabled:opacity-50"
                        onClick={handleBuildKeywords}
                        disabled={applyingTemplates || generatingKeywords}
                        data-testid="build-keywords-btn"
                      >
                        <Wand2 className="w-3 h-3" />
                        {applyingTemplates || generatingKeywords ? "Building..." : "Build Keywords (Nearby + Templates)"}
                      </button>
                    </div>
                    <Textarea
                      id="seo_keywords"
                      value={formData.seo_keywords}
                      onChange={(e) => setFormData({ ...formData, seo_keywords: e.target.value })}
                      placeholder="total wireless [city], wireless store [city], prepaid phones [city]"
                      rows={4}
                      data-testid="location-seo-keywords"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      <b>Build Keywords</b> generates nearby-city + Total Wireless terms, then appends your custom keyword templates ({"{city}"}, {"{state}"}, {"{street_name}"}) — edit them in <Link to="/admin/settings" className="text-teal underline">Settings → SEO Keyword Templates</Link>.
                    </p>
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="seo_canonical_url">Canonical URL</Label>
                      <button type="button" className="text-[11px] text-teal hover:underline font-medium" onClick={() => setFormData((prev) => ({ ...prev, seo_canonical_url: generateCanonicalUrl(prev) }))} data-testid="regenerate-canonical">Regenerate</button>
                    </div>
                    <Input
                      id="seo_canonical_url"
                      value={formData.seo_canonical_url || ""}
                      onChange={(e) => setFormData({ ...formData, seo_canonical_url: e.target.value })}
                      placeholder="https://primemediawireless.com/locations/mi/southfield/25857-lahser-rd"
                      data-testid="location-canonical-url"
                    />
                    <p className="text-xs text-slate-500 mt-1">The official URL for this page. Prevents duplicate content issues.</p>
                  </div>

                  <div className="border-2 border-teal/30 bg-teal/5 rounded-lg p-4 space-y-4" data-testid="local-seo-block">
                    <div>
                      <p className="text-sm font-semibold text-navy">Local SEO &mdash; Google Business + Service Area</p>
                      <p className="text-xs text-slate-500 mt-1">These fields power the Store schema, the star-rating display on the store page (visitors only) and the &ldquo;Driving directions from {"{city}"}&rdquo; links. Zip codes and nearby cities fill in automatically from the store&rsquo;s zip code when left blank.</p>
                      <Button type="button" size="sm" variant="outline" className="mt-2" disabled={fillingLocalSeo || !formData.zip_code} onClick={fillLocalSeo} data-testid="local-seo-autofill-btn">
                        {fillingLocalSeo ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1.5" />}
                        Fill zip codes &amp; nearby cities automatically
                      </Button>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label htmlFor="google_rating">Google Rating (0-5)</Label>
                        <Input
                          id="google_rating"
                          type="number"
                          step="0.1"
                          min="0"
                          max="5"
                          value={formData.google_rating ?? ""}
                          onChange={(e) => setFormData({ ...formData, google_rating: e.target.value })}
                          placeholder="4.8"
                          data-testid="location-google-rating"
                        />
                      </div>
                      <div>
                        <Label htmlFor="google_review_count">Review Count</Label>
                        <Input
                          id="google_review_count"
                          type="number"
                          min="0"
                          value={formData.google_review_count ?? ""}
                          onChange={(e) => setFormData({ ...formData, google_review_count: e.target.value })}
                          placeholder="47"
                          data-testid="location-google-review-count"
                        />
                      </div>
                      <div>
                        <Label htmlFor="price_range">Price Range</Label>
                        <select
                          id="price_range"
                          value={formData.price_range || "$"}
                          onChange={(e) => setFormData({ ...formData, price_range: e.target.value })}
                          className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                          data-testid="location-price-range"
                        >
                          <option value="$">$</option>
                          <option value="$$">$$</option>
                          <option value="$$$">$$$</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="google_maps_url">Google Business Profile URL</Label>
                      <Input
                        id="google_maps_url"
                        value={formData.google_maps_url || ""}
                        onChange={(e) => setFormData({ ...formData, google_maps_url: e.target.value })}
                        placeholder="https://maps.google.com/maps?cid=3841146404035567528"
                        data-testid="location-google-maps-url"
                      />
                      <p className="text-xs text-slate-500 mt-1">Paste the share link from your Google Business Profile. Used as <code>hasMap</code> + clickable directions.</p>
                    </div>
                    <div>
                      <Label htmlFor="service_area_zips">Service Area Zip Codes</Label>
                      <Input
                        id="service_area_zips"
                        value={(formData.service_area_zips || []).join(", ")}
                        onChange={(e) => setFormData({ ...formData, service_area_zips: e.target.value.split(",").map((z) => z.trim()).filter(Boolean) })}
                        placeholder="48034, 48075, 48076, 48237"
                        data-testid="location-service-area-zips"
                      />
                      <p className="text-xs text-slate-500 mt-1">Comma-separated. Emitted as <code>areaServed</code> + visible "service area" paragraph.</p>
                    </div>
                    <div>
                      <Label htmlFor="nearby_cities_seo">Nearby Cities (for Driving Directions Links)</Label>
                      <Input
                        id="nearby_cities_seo"
                        value={(formData.nearby_cities_seo || []).join(", ")}
                        onChange={(e) => setFormData({ ...formData, nearby_cities_seo: e.target.value.split(",").map((c) => c.trim()).filter(Boolean) })}
                        placeholder="Detroit, Royal Oak, Birmingham, Farmington Hills"
                        data-testid="location-nearby-cities"
                      />
                      <p className="text-xs text-slate-500 mt-1">Comma-separated city names. Each becomes a Google Maps directions deep link on the page (huge for "{"{city}"} to {"{store}"}" searches).</p>
                    </div>
                    <div>
                      <Label htmlFor="payment_methods">Accepted Payment Methods</Label>
                      <Input
                        id="payment_methods"
                        value={(formData.payment_methods || []).join(", ")}
                        onChange={(e) => setFormData({ ...formData, payment_methods: e.target.value.split(",").map((p) => p.trim()).filter(Boolean) })}
                        placeholder="Cash, Visa, Mastercard, Amex, Discover, Apple Pay"
                        data-testid="location-payment-methods"
                      />
                    </div>
                  </div>

                  <div className="border border-slate-200 rounded-lg p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-navy">Open Graph Tags (Social Media Sharing)</p>
                      <button type="button" className="text-[11px] text-teal hover:underline font-medium" onClick={() => {
                        const og = generateOgTags(formData);
                        setFormData((prev) => ({ ...prev, ...og }));
                      }} data-testid="regenerate-og">Regenerate All</button>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <Label htmlFor="og_title">OG Title</Label>
                        <Input
                          id="og_title"
                          value={formData.og_title || ""}
                          onChange={(e) => setFormData({ ...formData, og_title: e.target.value })}
                          placeholder="Total Wireless Southfield, MI | Prime Media Wireless"
                          data-testid="location-og-title"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label htmlFor="og_description">OG Description</Label>
                        <Textarea
                          id="og_description"
                          value={formData.og_description || ""}
                          onChange={(e) => setFormData({ ...formData, og_description: e.target.value })}
                          placeholder="Visit Total Wireless in Southfield, MI. No-contract plans, devices, and accessories."
                          rows={2}
                          data-testid="location-og-description"
                        />
                      </div>
                      <div>
                        <Label htmlFor="og_image">OG Image URL</Label>
                        <Input
                          id="og_image"
                          value={formData.og_image || ""}
                          onChange={(e) => setFormData({ ...formData, og_image: e.target.value })}
                          placeholder="https://primemediawireless.com/..."
                          data-testid="location-og-image"
                        />
                      </div>
                      <div>
                        <Label htmlFor="og_type">OG Type</Label>
                        <Input
                          id="og_type"
                          value={formData.og_type || "website"}
                          onChange={(e) => setFormData({ ...formData, og_type: e.target.value })}
                          data-testid="location-og-type"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="seo_schema">Structured Data (Schema.org JSON-LD)</Label>
                      <button type="button" className="text-[11px] text-teal hover:underline font-medium" onClick={() => setFormData((prev) => ({ ...prev, seo_schema: generateSeoSchema(prev) }))} data-testid="regenerate-seo-schema">Regenerate</button>
                    </div>
                    <Textarea
                      id="seo_schema"
                      value={formData.seo_schema}
                      onChange={(e) => setFormData({ ...formData, seo_schema: e.target.value })}
                      placeholder='{"@context": "https://schema.org", "@type": "ElectronicsStore", ...}'
                      rows={16}
                      className="font-mono text-xs"
                      data-testid="location-seo-schema"
                    />
                    <p className="text-xs text-slate-500 mt-1">This JSON-LD schema will be injected into the location's page for search engines.</p>
                  </div>
                </TabsContent>

                <TabsContent value="faqs" className="space-y-4" data-testid="location-faqs-content">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-navy">Store FAQs</p>
                      <p className="text-xs text-slate-500">Shown on the public location page + emitted as FAQPage JSON-LD for Google rich results.</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        className="text-[11px] text-teal hover:underline font-medium disabled:opacity-50"
                        onClick={handleAiGenerateFaqs}
                        disabled={aiFaqsLoading}
                        data-testid="ai-generate-faqs-btn"
                      >
                        {aiFaqsLoading ? "Generating..." : "✨ Generate 5 with AI"}
                      </button>
                      <button
                        type="button"
                        className="text-[11px] text-teal hover:underline font-medium"
                        onClick={() => setFormData((prev) => ({ ...prev, faqs: [...(prev.faqs || []), { question: "", answer: "" }] }))}
                        data-testid="add-faq-btn"
                      >
                        + Add FAQ
                      </button>
                    </div>
                  </div>

                  {(!formData.faqs || formData.faqs.length === 0) && (
                    <div className="border border-dashed border-slate-300 rounded-lg p-6 text-center">
                      <p className="text-sm text-slate-500">No FAQs yet. Click <b>Generate 5 with AI</b> to create a starter set, or add manually.</p>
                    </div>
                  )}

                  <div className="space-y-3">
                    {(formData.faqs || []).map((faq, idx) => (
                      <div key={idx} className="border border-slate-200 rounded-lg p-3 bg-slate-50" data-testid={`location-faq-editor-${idx}`}>
                        <div className="flex items-start gap-2">
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-teal/10 text-teal text-xs font-bold shrink-0 mt-1">
                            {idx + 1}
                          </span>
                          <div className="flex-1 space-y-2">
                            <div>
                              <Label className="text-xs">Question</Label>
                              <Input
                                value={faq.question || ""}
                                onChange={(e) => {
                                  const copy = [...formData.faqs];
                                  copy[idx] = { ...copy[idx], question: e.target.value };
                                  setFormData({ ...formData, faqs: copy });
                                }}
                                placeholder="e.g. Do I need an appointment to activate a phone?"
                                data-testid={`location-faq-question-${idx}`}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Answer</Label>
                              <Textarea
                                value={faq.answer || ""}
                                onChange={(e) => {
                                  const copy = [...formData.faqs];
                                  copy[idx] = { ...copy[idx], answer: e.target.value };
                                  setFormData({ ...formData, faqs: copy });
                                }}
                                placeholder="Answer in 2-3 sentences, specific to this store..."
                                rows={3}
                                data-testid={`location-faq-answer-${idx}`}
                              />
                            </div>
                          </div>
                          <button
                            type="button"
                            className="text-slate-400 hover:text-red-500 transition-colors"
                            onClick={() => {
                              const copy = [...formData.faqs];
                              copy.splice(idx, 1);
                              setFormData({ ...formData, faqs: copy });
                            }}
                            data-testid={`location-faq-delete-${idx}`}
                            aria-label="Delete FAQ"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="vidapay" className="space-y-6">
                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-sm text-navy font-body font-semibold">External Systems</p>
                    <p className="text-xs text-slate-600 mt-1 font-body">
                      Credentials for third-party platforms this location uses. Auto-populate in the dealer's portal and appear on the Dealers → External Systems tab.
                    </p>
                  </div>

                  {/* VidaPay sub-section */}
                  <div>
                    <div className="p-3 bg-blue-50 rounded-lg mb-3">
                      <p className="text-sm text-blue-800 font-body font-medium">VidaPay</p>
                      <p className="text-xs text-blue-700 mt-1 font-body">
                        VidaPay carrier activation credentials for this location.
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="vidapay_status">Status</Label>
                        <select
                          id="vidapay_status"
                          value={formData.vidapay_status || ""}
                          onChange={(e) => setFormData({ ...formData, vidapay_status: e.target.value })}
                          className="w-full mt-1 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                          data-testid="location-vidapay-status"
                        >
                          <option value="">Not Set</option>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                          <option value="cash_only">Cash Only</option>
                        </select>
                      </div>
                      <div>
                        <Label htmlFor="vidapay_tsp_id">TSP ID</Label>
                        <Input
                          id="vidapay_tsp_id"
                          value={formData.vidapay_tsp_id || ""}
                          onChange={(e) => setFormData({ ...formData, vidapay_tsp_id: e.target.value })}
                          placeholder="VidaPay TSP ID"
                          data-testid="location-vidapay-tsp-id"
                        />
                      </div>
                      <div>
                        <Label htmlFor="vidapay_username">Username</Label>
                        <Input
                          id="vidapay_username"
                          value={formData.vidapay_username || ""}
                          onChange={(e) => setFormData({ ...formData, vidapay_username: e.target.value })}
                          placeholder="VidaPay username"
                          data-testid="location-vidapay-username"
                        />
                      </div>
                      <div>
                        <Label htmlFor="vidapay_password">Password</Label>
                        <Input
                          id="vidapay_password"
                          value={formData.vidapay_password || ""}
                          onChange={(e) => setFormData({ ...formData, vidapay_password: e.target.value })}
                          placeholder="VidaPay password"
                          data-testid="location-vidapay-password"
                        />
                      </div>
                    </div>
                  </div>

                  {/* B2BSoft sub-section */}
                  <div>
                    <div className="p-3 bg-purple-50 rounded-lg mb-3">
                      <p className="text-sm text-purple-800 font-body font-medium">B2BSoft</p>
                      <p className="text-xs text-purple-700 mt-1 font-body">
                        B2BSoft POS credentials for this location — used to authenticate the store's POS to the B2BSoft API.
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="b2bsoft_client_id">Client ID</Label>
                        <Input
                          id="b2bsoft_client_id"
                          value={formData.b2bsoft_client_id || ""}
                          onChange={(e) => setFormData({ ...formData, b2bsoft_client_id: e.target.value })}
                          placeholder="B2BSoft Client ID"
                          data-testid="location-b2bsoft-client-id"
                        />
                      </div>
                      <div>
                        <Label htmlFor="b2bsoft_access_code">Access Code</Label>
                        <Input
                          id="b2bsoft_access_code"
                          value={formData.b2bsoft_access_code || ""}
                          onChange={(e) => setFormData({ ...formData, b2bsoft_access_code: e.target.value })}
                          placeholder="B2BSoft Access Code"
                          data-testid="location-b2bsoft-access-code"
                        />
                      </div>
                      <div>
                        <Label htmlFor="b2bsoft_username">Username</Label>
                        <Input
                          id="b2bsoft_username"
                          value={formData.b2bsoft_username || ""}
                          onChange={(e) => setFormData({ ...formData, b2bsoft_username: e.target.value })}
                          placeholder="B2BSoft username"
                          data-testid="location-b2bsoft-username"
                        />
                      </div>
                      <div>
                        <Label htmlFor="b2bsoft_password">Password</Label>
                        <Input
                          id="b2bsoft_password"
                          value={formData.b2bsoft_password || ""}
                          onChange={(e) => setFormData({ ...formData, b2bsoft_password: e.target.value })}
                          placeholder="B2BSoft password"
                          data-testid="location-b2bsoft-password"
                        />
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="repair" className="space-y-4">
                  <div className="p-4 bg-amber-50 rounded-lg mb-4">
                    <p className="text-sm text-amber-800 font-body font-medium">Device Repair Program</p>
                    <p className="text-xs text-amber-700 mt-1 font-body">
                      Manage this location's participation in the Device Repair program. Locations must complete training before being approved to order repair parts.
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="repair_status">Repair Status</Label>
                    <select
                      id="repair_status"
                      value={formData.repair_status || "none"}
                      onChange={(e) => setFormData({ ...formData, repair_status: e.target.value })}
                      className="w-full mt-1 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                      data-testid="location-repair-status"
                    >
                      <option value="none">Not Enrolled</option>
                      <option value="pending">Pending</option>
                      <option value="training">In Training</option>
                      <option value="approved">Approved</option>
                    </select>
                    <div className="mt-3 space-y-2 text-xs text-slate-500 font-body">
                      <p><strong>Not Enrolled:</strong> Location has not opted into device repair</p>
                      <p><strong>Pending:</strong> Location has requested to join the repair program</p>
                      <p><strong>In Training:</strong> Location is completing repair training (can access repair KB & training content)</p>
                      <p><strong>Approved:</strong> Location can order device parts and tools from the marketplace</p>
                    </div>
                  </div>
                </TabsContent>

                <div className="mt-6 pt-4 border-t">
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-teal hover:bg-teal-dark text-white"
                    data-testid="submit-location-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {editingLocation ? "Updating..." : "Creating..."}
                      </>
                    ) : (
                      editingLocation ? "Update Location" : "Create Location"
                    )}
                  </Button>
                </div>
              </form>
            </Tabs>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
